import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { recurringService } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Calendar, DollarSign, Clock, Loader2 } from 'lucide-react';

const FREQ_LABELS = {
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  quarterly: 'Trimestral'
};

const FREQ_COLORS = {
  weekly: 'bg-blue-100 text-blue-800',
  biweekly: 'bg-violet-100 text-violet-800',
  monthly: 'bg-emerald-100 text-emerald-800',
  quarterly: 'bg-amber-100 text-amber-800'
};

export const RecurringBillingDialog = ({ open, onOpenChange, debtor, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    frequency: 'monthly',
    amount: 0,
    start_date: new Date().toISOString().split('T')[0]
  });
  const [currentConfig, setCurrentConfig] = useState(null);

  useEffect(() => {
    if (open && debtor?.id) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debtor?.id]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await recurringService.get(debtor.id);
      const data = res.data;
      if (data && data.enabled !== undefined) {
        setConfig({
          enabled: data.enabled,
          frequency: data.frequency || 'monthly',
          amount: data.amount || 0,
          start_date: data.start_date || new Date().toISOString().split('T')[0]
        });
        setCurrentConfig(data);
      } else {
        setConfig({ enabled: false, frequency: 'monthly', amount: debtor.total_debt || 0, start_date: new Date().toISOString().split('T')[0] });
        setCurrentConfig(null);
      }
    } catch {
      setConfig({ enabled: false, frequency: 'monthly', amount: debtor.total_debt || 0, start_date: new Date().toISOString().split('T')[0] });
      setCurrentConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (config.enabled && config.amount <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    setSaving(true);
    try {
      await recurringService.update(debtor.id, config);
      toast.success(config.enabled ? 'Cobro recurrente activado' : 'Cobro recurrente desactivado');
      onUpdate?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    } catch { return dateStr; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="recurring-billing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-teal-600" />
            Cobro Recurrente
          </DialogTitle>
          <DialogDescription>
            Configurar cobro automático para {debtor?.name}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5">
            {/* Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Activar cobro recurrente</p>
                <p className="text-sm text-slate-500">Se generará un cobro automático según la frecuencia</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(v) => setConfig(prev => ({ ...prev, enabled: v }))}
                data-testid="recurring-toggle"
              />
            </div>

            {config.enabled && (
              <>
                {/* Amount */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4 text-slate-500" /> Monto por cobro
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={config.amount}
                    onChange={(e) => setConfig(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    data-testid="recurring-amount"
                  />
                </div>

                {/* Frequency */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-slate-500" /> Frecuencia
                  </Label>
                  <Select value={config.frequency} onValueChange={(v) => setConfig(prev => ({ ...prev, frequency: v }))}>
                    <SelectTrigger data-testid="recurring-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="biweekly">Quincenal</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                      <SelectItem value="quarterly">Trimestral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start date */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-slate-500" /> Fecha de inicio
                  </Label>
                  <Input
                    type="date"
                    value={config.start_date}
                    onChange={(e) => setConfig(prev => ({ ...prev, start_date: e.target.value }))}
                    data-testid="recurring-start-date"
                  />
                </div>

                {/* Current status */}
                {currentConfig && currentConfig.enabled && (
                  <Card className="border-teal-200 bg-teal-50/50">
                    <CardContent className="p-4 space-y-2">
                      <p className="text-sm font-medium text-teal-800">Estado actual</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-slate-500">Próximo cobro:</span>
                          <p className="font-medium">{formatDate(currentConfig.next_billing_date)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Último cobro:</span>
                          <p className="font-medium">{formatDate(currentConfig.last_billed_date)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Cobros realizados:</span>
                          <p className="font-medium">{currentConfig.billing_count || 0}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Total cobrado:</span>
                          <p className="font-medium">${(currentConfig.total_billed || 0).toLocaleString('es-AR')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Save */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="recurring-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Badge component to show recurring status inline
export const RecurringBadge = ({ recurringBilling }) => {
  if (!recurringBilling?.enabled) return null;
  return (
    <Badge className={`${FREQ_COLORS[recurringBilling.frequency]} text-xs font-medium`} data-testid="recurring-badge">
      <RefreshCw className="w-3 h-3 mr-1" />
      {FREQ_LABELS[recurringBilling.frequency]}
    </Badge>
  );
};

export default RecurringBillingDialog;
